import axios from '../../utils/axiosConfig';
import { authRefreshed } from './auth';

// Action Types
export const GET_PROMOTER_REQUEST = 'GET_PROMOTER_REQUEST';
export const GET_PROMOTER_SUCCESS = 'GET_PROMOTER_SUCCESS';
export const GET_PROMOTER_FAIL = 'GET_PROMOTER_FAIL';

export const UPDATE_PROMOTER_REQUEST = 'UPDATE_PROMOTER_REQUEST';
export const UPDATE_PROMOTER_SUCCESS = 'UPDATE_PROMOTER_SUCCESS';
export const UPDATE_PROMOTER_FAILURE = 'UPDATE_PROMOTER_FAILURE';

// Dados Financeiros: the signed-in promoter's own revenue and repass summary.
// Its own loading flag, deliberately — the shared `loading` on this slice
// drives the Dashboard's full-page blocking spinner, and a background refresh
// of one tab must never blank the whole dashboard.
export const GET_PROMOTER_FINANCIALS_REQUEST = 'GET_PROMOTER_FINANCIALS_REQUEST';
export const GET_PROMOTER_FINANCIALS_SUCCESS = 'GET_PROMOTER_FINANCIALS_SUCCESS';
export const GET_PROMOTER_FINANCIALS_FAIL = 'GET_PROMOTER_FINANCIALS_FAIL';

export const CLEAR_PROMOTER = 'CLEAR_PROMOTER';
export const clearPromoter = () => ({ type: CLEAR_PROMOTER });

// The SIGNED-IN promoter's own profile, kept apart from `promoter` above.
//
// `promoter` is whichever profile was fetched last — an event page loads the
// host promoter's, a brand page someone else's. Anything that asks "what is
// true of the person using the app?" (the Easypay gate, T2B-423) has to read a
// slice that a stranger's profile can never land in, or it reads their account
// id as our own and unlocks the wrong thing.
export const GET_MY_PROMOTER_SUCCESS = 'GET_MY_PROMOTER_SUCCESS';

// Requests currently in flight, keyed by promoter id.
//
// Two callers now ask for the signed-in promoter's profile on the dashboard —
// the page itself and AuthBootstrapper, which needs it everywhere for the
// Easypay gate (T2B-423). Both fire in the same tick, so neither can see the
// other's `loading` flag. Sharing the promise makes that one request instead
// of two, and quietly halves StrictMode's double-effect fetches in dev.
const inFlight = new Map();

// Fetch a promoter profile
export const getPromoter = (promoterId) => async (dispatch, getState) => {
  const pending = inFlight.get(String(promoterId));
  if (pending) return pending;

  const run = fetchPromoter(promoterId, dispatch, getState).finally(() => {
    inFlight.delete(String(promoterId));
  });
  inFlight.set(String(promoterId), run);
  return run;
};

const fetchPromoter = async (promoterId, dispatch, getState) => {
  dispatch({ type: GET_PROMOTER_REQUEST });
  try {
    const { auth } = getState();
    const token = auth?.token;

    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // The endpoint serves a small PUBLIC card by default — an event page only
    // needs the host's name and avatar, and nobody should be handed a
    // stranger's IBAN. `?view=self` asks for the full profile and is refused
    // unless the token proves we're that promoter, so we only ask when we
    // believe we are. A stale token comes back 401 and the axios interceptor
    // refreshes and retries; it never degrades to the public card behind our
    // back, which would blank the dashboard's billing fields and make the
    // Easypay gate think we were unconnected.
    const isOwnProfile =
      auth?.user?.role === 'promoter' &&
      String(auth?.user?.id) === String(promoterId);

    const res = await axios.get(
      `/promoters/profile/${promoterId}${isOwnProfile ? '?view=self' : ''}`,
      { headers }
    );

    dispatch({ type: GET_PROMOTER_SUCCESS, payload: res.data });

    // The same fetch feeds both slices when it's our own profile, so the
    // Dashboard's existing call keeps `me` fresh without a second request.
    if (isOwnProfile) {
      dispatch({ type: GET_MY_PROMOTER_SUCCESS, payload: res.data });
    }
  } catch (err) {
    dispatch({
      type: GET_PROMOTER_FAIL,
      payload: err?.response?.data?.message || 'Failed to fetch promoter',
    });
  }
};

// Update promoter profile (supports FormData with files OR plain object)
// Always sends multipart/form-data so file uploads work seamlessly.
export const updatePromoter = (payload) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_PROMOTER_REQUEST });

  try {
    const { auth } = getState();
    const token = auth?.token;
    const promoterId = auth?.user?.id;

    // Normalize to FormData
    let body;
    if (typeof FormData !== 'undefined' && payload instanceof FormData) {
      body = payload;
    } else {
      body = new FormData();
      Object.entries(payload || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null) body.append(k, v);
      });
    }

    const res = await axios.put(`/promoters/profile/${promoterId}`, body, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    dispatch({ type: UPDATE_PROMOTER_SUCCESS, payload: res.data });
    dispatch({ type: GET_MY_PROMOTER_SUCCESS, payload: res.data });

    const current = auth?.user || {};
    const bust = Date.now();
    const mergedUser = {
      ...current,
      ...(res.data.avatar
        ? { avatar: res.data.avatar, avatarUpdatedAt: bust }
        : {}),
      ...(res.data.username ? { username: res.data.username } : {}),
    };
    dispatch(authRefreshed({ token, user: mergedUser }));

    return res.data;
  } catch (error) {
    dispatch({
      type: UPDATE_PROMOTER_FAILURE,
      payload: error?.response?.data?.error || 'Failed to update promoter',
    });
    throw error;
  }
};

/**
 * Fetch the signed-in promoter's financial summary.
 *
 * No id argument on purpose: the endpoint scopes itself to the token, so
 * there is nothing here that could be pointed at somebody else's revenue.
 */
export const getPromoterFinancials = () => async (dispatch) => {
  dispatch({ type: GET_PROMOTER_FINANCIALS_REQUEST });

  try {
    const res = await axios.get('/promoters/financials');
    dispatch({ type: GET_PROMOTER_FINANCIALS_SUCCESS, payload: res.data });
    return res.data;
  } catch (error) {
    dispatch({
      type: GET_PROMOTER_FINANCIALS_FAIL,
      payload:
        error?.response?.data?.message || 'Failed to fetch financial data',
    });
    throw error;
  }
};
